<template>
    <div>
      <breadcrumb :items="breadcrumbs" />
      <v-container fluid>
        <div class="d-flex mb-8">
          <h2 class="font-weight-bolder mb-6">{{ $t('finance.banks.title') }}</h2>
          <div class="d-flex ml-auto">
            <div class="d-flex mt-1">
              <v-spacer></v-spacer>
              <v-btn color="primary" dark class="mr-2" @click="handleCreateBank">
                {{ $t('finance.banks.addNewBank') }}
              </v-btn>
            </div>
          </div>
        </div>
        <!-- Data Table Card -->
        <v-card elevation="0" class="px-5 mt-7">
          <v-card-text>
            <v-row style="margin-left: -30px; margin-bottom: 20px; margin-top: 15px;">
              <v-col cols="12">
                <v-data-table :headers="tableHeaders" :items="banksList" :items-per-page="options.limit"
                  :footer-props="{ 'items-per-page-options': itemsPerPageOptions }" item-key="id" hide-default-footer
                  :loading="loading" :options.sync="options" :server-items-length="totalBanks"
                  :loading-text="$t('loadingData')" @click:row="handleRowClick">
                  <template v-slot:top>
                    <div class="d-flex">
                      <!-- Search Field -->
                      <div class="ml-3" style="width: 280px;">
                        <label for="text">&nbsp;</label>
                        <v-text-field prepend-inner-icon="mdi-magnify" outlined :placeholder="$t('Search')" dense
                          v-model="search" @input="searchQuery" class="shrink"></v-text-field>
                      </div>
                      <v-spacer></v-spacer>
                      <!-- Pagination Info -->
                      <div class="py-1 title">
                        {{ fromBanks }} - {{ toBanks }} {{ $t("of") }} {{ totalBanks }}
                      </div>
                      <!-- Pagination Controls -->
                      <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page <= 1"
                        @click="pageChange(false)">
                        <v-icon dark>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page >= totalPages"
                        @click="pageChange(true)">
                        <v-icon dark>mdi-chevron-right</v-icon>
                      </v-btn>
                      <!-- Customization Menu -->
                      <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                            <v-icon dark>mdi-cog-outline</v-icon>
                          </v-btn>
                        </template>
                        <v-card width="417">
                          <v-list>
                            <v-list-item>
                              <v-list-item-title>{{ $t("customization") }}</v-list-item-title>
                            </v-list-item>
                            <v-divider></v-divider>
                            <div class="pl-3 pt-2 font-weight-normal">
                              {{ $t("recordsPerPage") }}
                            </div>
                            <v-list-item>
                              <v-slider v-model="itemsPerPageSlider" :tick-labels="itemsPerPageOptions" :max="3" step="1"
                                ticks="always" tick-size="4"></v-slider>
                            </v-list-item>
                            <v-divider class="mt-n3"></v-divider>
                          </v-list>
                          <v-card-text>
                            <v-container fluid>
                              <v-row>
                                <v-col class="mt-n9" cols="6" v-for="header in headers" :key="header.value">
                                  <v-checkbox v-if="header.text" v-model="selectedHeaders" :label="header.text"
                                    color="primary" :value="header.text" hide-details></v-checkbox>
                                </v-col>
                              </v-row>
                            </v-container>
                          </v-card-text>
                          <v-layout justify-center>
                            <v-card-actions class="mb-5">
                              <v-spacer></v-spacer>
                              <v-btn outlined color="primary" @click="resetDefault" width="190">
                                {{ $t("restoreDefault") }}
                              </v-btn>
                              <v-btn color="primary" @click="resetTableStructure" width="190">
                                {{ $t("apply") }}
                              </v-btn>
                            </v-card-actions>
                          </v-layout>
                        </v-card>
                      </v-menu>
                      <!-- Export and Print Buttons -->
                      <v-tooltip top color="black" max-width="350">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on"
                            @click="exportToPDF">
                            <v-icon dark>mdi-download</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("download") }}</span>
                      </v-tooltip>
                      <v-tooltip top color="black" max-width="350">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on"
                            @click="printPDF">
                            <v-icon dark>mdi-printer</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("print") }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <!-- Bank Title Column -->
                  <template v-slot:item.bankTitle="{ item }">
                      <div class="d-flex align-center">
                        <img
                          :src="item.logo"
                          style="width: 40px; height: 40px; border-radius: 8px; object-fit: contain;" :alt="$t('finance.banks.bankLogo')"
                          class="mr-3">
                        <span class="text-body-2 font-weight-medium">{{ item.bankTitle }}</span>
                      </div>
                    </template>
                  <template v-slot:footer></template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>
    </div>
  </template>
  
  <script>
  import loadingMixin from "@/mixins/LoadingMixin";
  import download from "@/mixins/DownloadMixin";
  import html2pdf from "html2pdf.js";
  import printJS from "print-js";
  import FinanceService from "@/_services/FinanceService";
  
  export default {
    created() {
      this.fetchBanks();
    },
    async mounted() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
    },
    computed: {
      totalPages() {
        return parseInt(Math.ceil(this.totalBanks / this.options.limit));
      },
      filteredBanks() {
        let banks = [...this.allBanks];
  
        // Filter by search
        if (this.search) {
          const searchLower = this.search.toLowerCase();
          banks = banks.filter(bank =>
            bank.bankTitle.toLowerCase().includes(searchLower) ||
            bank.contactPerson.toLowerCase().includes(searchLower) ||
            bank.email.toLowerCase().includes(searchLower) ||
            bank.phone.toLowerCase().includes(searchLower)
          );
        }
  
        return banks;
      },
    },
    data() {
      return {
        debounceTimer: null,
        fromBanks: 0,
        toBanks: 0,
        search: "",
        loading: false,
        options: {
          limit: 10,
          page: 1,
        },
        headers: [
          {
            text: this.$t("finance.banks.bankTitle"),
            align: "start",
            value: "bankTitle",
            class: "black--text",
            sortable: true,
          },
          {
            text: this.$t("finance.banks.contactPerson"),
            align: "start",
            value: "contactPerson",
            class: "black--text",
            sortable: true,
          },
          {
            text: this.$t("finance.banks.email"),
            align: "start",
            value: "email",
            class: "black--text",
            sortable: true,
          },
          {
            text: this.$t("finance.banks.phone"),
            align: "start",
            value: "phone",
            class: "black--text",
            sortable: true,
          },
        ],
        allSelectedHeaders: [
          this.$t("finance.banks.bankTitle"),
          this.$t("finance.banks.contactPerson"),
          this.$t("finance.banks.email"),
          this.$t("finance.banks.phone"),
        ],
        menu: false,
        breadcrumbs: [
          {
            text: this.$t("finance.banks.management"),
            disabled: false,
            href: "/finance-management",
            exact: true,
          },
          {
            text: this.$t("finance.banks.title"),
            disabled: false,
            href: "/finance-management/banks",
          },
        ],
        allBanks: [],
        banksList: [],
        totalBanks: 0,
        itemsPerPageOptions: [5, 10, 25, 50],
        itemsPerPageSlider: 1,
        selectedHeaders: [],
        tableHeaders: [],
      };
    },
    methods: {
      async fetchBanks() {
        this.startLoading();
        try {
          const params = {
            page: this.options.page,
            limit: this.options.limit,
            search: this.search,
          };

          const response = await FinanceService.getBanks(params);
          const banksData = response.data.rows || [];

          this.allBanks = Array.isArray(banksData) ? banksData.map(bank => ({
            id: bank.id,
            bankTitle: bank.name || this.$t('finance.banks.notAvailable'),
            logo: bank.logo || '',
            contactPerson: [bank.contactFirstName, bank.contactLastName].filter(Boolean).join(' ') || this.$t('finance.banks.notAvailable'),
            email: bank.contactEmail || this.$t('finance.banks.notAvailable'),
            phone: bank.contactPhoneNo || this.$t('finance.banks.notAvailable'),
            description: bank.description || '',
            require2fa: bank.require2fa || false,
            cooperativeId: bank.cooperativeId,
            createdAt: bank.createdAt,
            updatedAt: bank.updatedAt,
          })) : [];

          this.banksList = this.allBanks;
          this.totalBanks = this.allBanks.length;
          this.fromBanks = this.totalBanks > 0 ? (this.options.page - 1) * this.options.limit + 1 : 0;
          this.toBanks = Math.min(this.options.page * this.options.limit, this.totalBanks);

        } catch (error) {
          console.error('Error fetching banks:', error);
          this.allBanks = [];
          this.banksList = [];
          this.totalBanks = 0;
          this.fromBanks = 0;
          this.toBanks = 0;
        } finally {
          this.stopLoading();
        }
      },
      async resetDefault() {
        this.tableHeaders = this.headers;
        this.selectedHeaders = this.allSelectedHeaders;
        this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
        this.menu = false;
        this.fetchBanks();
      },
      async resetTableStructure() {
        this.tableHeaders = this.headers.filter((header) =>
          this.selectedHeaders.includes(header.text)
        );
        this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
        this.fetchBanks();
      },
      searchQuery() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.options.page = 1;
          this.fetchBanks();
        }, 500);
      },
      async exportToPDF() {
        this.startLoading();
        try {
          const element = document.querySelector(".v-data-table__wrapper");
          let opt = {
            margin: 1,
            filename: "Banks.pdf",
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
          };
          await html2pdf().set(opt).from(element).save();
        } catch (error) {
          console.error("Export error:", error);
        } finally {
          this.stopLoading();
        }
      },
      async printPDF() {
        const element = document.querySelector(".v-data-table__wrapper");
        let opt = {
          margin: 1,
          filename: "Banks.pdf",
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
        };
        let worker = await html2pdf()
          .set(opt)
          .from(element)
          .toCanvas()
          .outputPdf("blob", "Banks.pdf");
        let file = new File([worker], "Banks.pdf", { type: "application/pdf" });
        var pdfUrl = await URL.createObjectURL(file);
        await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
      },
      pageChange(next) {
        if (next) this.options.page += 1;
        else this.options.page -= 1;
        this.fetchBanks();
      },
      handleCreateBank() {
        // Navigate to create bank page
        this.$router.push({ name: "createBank" });
      },
      handleRowClick(item) {
        this.$router.push({ name: 'bankDetails', params: { id: item.id } });
      },
    },
    mixins: [loadingMixin, download],
  };
  </script>
  
  <style lang="scss" scoped>
  ::v-deep .v-data-table-header span {
    text-transform: uppercase !important;
    color: black;
    font-weight: bold;
  }
  
  ::v-deep .v-data-table__wrapper tbody tr {
    cursor: pointer;
  }
  </style>